import _ from 'lodash'
import { v4 } from 'uuid'
import { IDataFrame } from '../definitions/DataAnalytics'
import { IKPICard } from '../definitions/Shared'
import { IApprovalAuditChanges } from '../definitions/Approval'
import algo from '../core/Algo'

const Helper = {
    isDev: () => process.env.NODE_ENV === 'development',
    platform: () => (navigator?.platform?.toLowerCase().includes('mac') || navigator?.userAgent?.toLowerCase().includes('mac')) ? 'Mac' : 'Other',
    fakeHTTP: (): Promise<number> => {
        return new Promise(resolve => {
            setTimeout(() => {
                console.log('API (fake): ---')
                resolve(Math.floor(Math.random() * 100))
            }, 2000)
        })
    },
    generateUUID: (prefix?: string) => {
        const uuid = v4()
        return prefix ? `${prefix}_${uuid}` : uuid
    },
    // All temporary ids are 0 with 16 decimal places.
    tempId: () => Helper.randomTempId(),
    randomTempId: (): number => {
        const random = Math.random()

        // Calculate the range between the two endpoints
        const range = 0.9 - 0.0000000001

        // Multiply the random number by the range and add the lower endpoint
        const result = _.round(random * range + 0.0000000001, 16)

        return result
    },
    checkRange: (score: number, min: number, max: number) => {
        return (score <= max || max === null || isNaN(max)) && (score >= min || min === null || isNaN(min))
    },
    trend: (delta: number, inverse?: boolean) => {
        if (inverse) {
            if (delta < 0) return { good: true, static: false, bad: false }
            if (delta === 0) return { good: false, static: true, bad: false }
            if (delta > 0) return { good: false, static: false, bad: true }
        } else {
            if (delta > 0) return { good: true, static: false, bad: false }
            if (delta === 0) return { good: false, static: true, bad: false }
            if (delta < 0) return { good: false, static: false, bad: true }
        }
    },
    dataframeToKPICard: (dataframe: IDataFrame): IKPICard => {
        if (dataframe)
            return { value: _.last(dataframe?.points)?.y ?? 0, df: dataframe }
        else
            return { value: 0, df: { points: [], delta: 0, deltaPercentage: 0 } }
    },
    deepEquals: (a: any, b: any): boolean => JSON.stringify(a) === JSON.stringify(b),
    hasVal: (val: any) => val !== null && val !== undefined,
    generateKeyOfObject: (obj: { [key: string]: any }): string => {
        const keys = Object.keys(obj).sort() // Sort keys alphabetically

        let result = ''
        for (const key of keys) {
            if (Array.isArray(obj[key])) {
                _.orderBy(obj[key]) // Sort the array numerically
                result += `${key}:[${obj[key].join(',')}]`
            } else {
                result += `${key}:${obj[key]};`
            }
        }
        return result
    },
    readCSSVariable: (name: string, querySelector?: string) => {
        let element = document.documentElement

        if (querySelector) {
            element = document.querySelector(querySelector)
        }

        return getComputedStyle(element).getPropertyValue(name)
    },
    readCSSProperty: (name: string, querySelector?: string) => {
        let element = document.documentElement

        if (querySelector) {
            element = document.querySelector(querySelector)
        }

        return getComputedStyle(element)[name]
    },
    hexToRgbA: (hex: string, alpha: number) => {
        let r = 0, g = 0, b = 0
        if (hex.length === 4) {
            r = parseInt(hex[1] + hex[1], 16)
            g = parseInt(hex[2] + hex[2], 16)
            b = parseInt(hex[3] + hex[3], 16)
        } else if (hex.length === 7) {
            r = parseInt(hex[1] + hex[2], 16)
            g = parseInt(hex[3] + hex[4], 16)
            b = parseInt(hex[5] + hex[6], 16)
        }
        return `rgba(${r},${g},${b},${alpha})`
    },
    hexWithAlpha: (hex: string, alpha: number) => {
        const r = parseInt(hex.substring(1, 3), 16)
        const g = parseInt(hex.substring(3, 5), 16)
        const b = parseInt(hex.substring(5, 7), 16)

        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`
    },
    getNestedObjectReference: (obj: any, nestedField: string) => {
        if (!Helper.hasVal(obj)) return null

        const splittedFields = nestedField.split('.')
        let result = obj

        for (let i = 0; i < splittedFields.length; i++) {
            const splitFiled = splittedFields[i]
            if (Helper.hasVal(result))
                result = result[splitFiled]
        }

        return result
    },
    getNestedValue: <T>(obj: T, path: string) => {
        if (!Helper.hasVal(obj)) return undefined

        const keys = path.split('.')
        let result = obj

        for (const key of keys) {
            if (Array.isArray(result)) result = result.map(x => x ? x[key] : null) as any
            else result = result ? result[key] : null

            if (result === undefined) {
                return undefined
            }
        }

        return result
    },
    setNestedValue: <T>(obj: T, path: string, newValue: any): T | undefined => {
        if (!Helper.hasVal(obj)) return undefined

        const keys = path.split('.')
        let result: any = obj

        // Traverse the object up to the second-to-last key
        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i]

            // Handle array case
            if (Array.isArray(result)) {
                result = result.map(x => (x ? x[key] : undefined))
            } else {
                if (!result[key]) {
                    result[key] = {} // Create a new object if key doesn't exist
                }
                result = result[key]
            }

            if (result === undefined) {
                return undefined // If at any point the result is undefined, return undefined
            }
        }

        // Set the value at the last key
        const lastKey = keys[keys.length - 1]
        if (Array.isArray(result)) {
            result.forEach((item: any) => {
                if (item) {
                    item[lastKey] = newValue
                }
            })
        } else {
            result[lastKey] = newValue
        }

        return obj // Return the modified object
    },
    isNumber: (v: any) => _.isNumber(v),
    rgbToHex: (color: string): string => {
        if (color.startsWith('#')) {
            return color
        }

        const rgbMatch = color.match(/rgb[a]?\((\d+),\s*(\d+),\s*(\d+)/)
        if (rgbMatch) {
            const r = parseInt(rgbMatch[1])
            const g = parseInt(rgbMatch[2])
            const b = parseInt(rgbMatch[3])
            const toHex = (n: number) => {
                const hex = n.toString(16)
                return hex.length === 1 ? '0' + hex : hex
            }

            return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
        }

        return '#FFFFFF'
    },
    getColorLuminance: (color: string): 'light' | 'dark' => {
        const hexToRgb = (hex: string) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
            return result
                ? {
                        r: parseInt(result[1], 16),
                        g: parseInt(result[2], 16),
                        b: parseInt(result[3], 16)
                    }
                : null
        }

        const getLuminance = (r: number, g: number, b: number) => {
            const [rs, gs, bs] = [r, g, b].map(c => {
                c = c / 255
                return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
            })
            return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
        }

        const rgb = hexToRgb(color)
        if (!rgb) return 'dark'

        const luminance = getLuminance(rgb.r, rgb.g, rgb.b)

        return luminance > 0.5 ? 'light' : 'dark'
    },
    // Format value for rendering (in ContractDetailsInfo)
    formatValue: (value: any, type?: string): string => {
        if (value == null) return 'N/A'

        switch (type) {
            case 'date':
                if (typeof value === 'string') {
                    const date = new Date(value)
                    return isNaN(date.getTime()) ? value : date.toLocaleDateString()
                }
                return value.toString()

            case 'number':
                return typeof value === 'number' ? value.toString() : (value?.toString() ?? 'N/A')

            case 'bool':
                return value === true ? 'Yes' : value === false ? 'No' : 'N/A'

            default:
                if (typeof value === 'object' && value?.name) {
                    return value.name
                }
                if (Array.isArray(value)) {
                    return value.map(item => typeof item === 'object' && item?.name ? item.name : item).join(', ')
                }
                return value?.toString() ?? 'N/A'
        }
    },
    // Extract the relative key from a full path (in ContractDetailsInfo)
    getRelativeKey: (fullKey: string, parentKey: string): string => {
        if (!parentKey) return fullKey
        const prefix = parentKey + '.'
        if (fullKey.startsWith(prefix)) {
            return fullKey.substring(prefix.length)
        }
        return fullKey
    },
    parsePrimitive: (val: string): any => {
        const trimmed = val.trim()
        const lower = trimmed.toLowerCase()

        if (lower === 'true') return true
        if (lower === 'false') return false

        if (!isNaN(Number(trimmed)) && trimmed !== '') return Number(trimmed)

        return trimmed
    },
    stableStringify: (value: any): string => {
        if (value === null || typeof value !== 'object') {
            return JSON.stringify(value)
        }

        if (Array.isArray(value)) {
            return `[${value.map(Helper.stableStringify).join(',')}]`
        }

        const keys = Object.keys(value).sort()
        const props = keys.map(key => `${JSON.stringify(key)}:${Helper.stableStringify(value[key])}`)
        return `{${props.join(',')}}`
    },
    /**
    * Safely converts AI-extracted date strings to a valid Date.
    * Accepts only real dates (YYYY-MM-DD or parsable formats) within 1900–2100.
    * Rejects invalid dates (e.g. "2025-13-45", "Feb 30", "next year") to prevent garbage in the contract.
    * Returns clean Date at noon UTC (timezone-safe).
    */
    parseSafeDate: (value: any): Date => {
        if (value == null) return null
        const str = String(value).trim()
        if (!str) return null
        if (str.length === 10 && str.charAt(4) === '-' && str.charAt(7) === '-') {
            const y = Number(str.substring(0, 4))
            const m = Number(str.substring(5, 7))
            const d = Number(str.substring(8, 10))
            if (y >= 1900 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
                const maxDays = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
                if (d <= maxDays[m - 1]) {
                    return new Date(y, m - 1, d, 12, 0, 0)
                }
            }
        }
        const date = new Date(str)
        if (isNaN(date.getTime())) return null
        const year = date.getFullYear()
        if (year < 1900 || year > 2100) return null

        return new Date(year, date.getMonth(), date.getDate(), 12, 0, 0)
    },
    compareItem: <T, Y>(oldValue: T | T[], newValue: Y | Y[], path?: string): IApprovalAuditChanges[] => {
        const changes: IApprovalAuditChanges[] = []

        const isOldDate = oldValue instanceof Date
        const isNewDate = newValue instanceof Date

        if (isOldDate || isNewDate) {
            const oldTime = isOldDate ? oldValue.getTime() : oldValue
            const newTime = isNewDate ? newValue.getTime() : newValue

            if (oldTime === newTime) return changes

            if (!Helper.hasVal(oldValue)) {
                changes.push({
                    type: 'create',
                    prevValue: null,
                    newValue: newValue ?? null,
                    path: path ?? ''
                })
            } else if (!Helper.hasVal(newValue)) {
                changes.push({
                    type: 'delete',
                    prevValue: oldValue ?? null,
                    newValue: null,
                    path: path ?? ''
                })
            } else {
                changes.push({
                    type: 'update',
                    prevValue: oldValue ?? null,
                    newValue: newValue ?? null,
                    path: path ?? ''
                })
            }

            return changes
        }

        if (Array.isArray(oldValue) && Array.isArray(newValue)) {
            const max = Math.max(oldValue.length, newValue.length)

            for (let i = 0; i < max; i++) {
                changes.push(
                    ...Helper.compareItem(oldValue[i], newValue[i], `${path ?? ''}[${i}]`)
                )
            }
        } else if (
            oldValue !== null &&
            newValue !== null &&
            typeof oldValue === 'object' &&
            typeof newValue === 'object'
        ) {
            const keys = algo.uniq([
                ...Object.keys(oldValue ?? {}),
                ...Object.keys(newValue ?? {})
            ])

            for (let i = 0; i < keys.length; i++) {
                const key = keys[i]

                const oldKeyValue = oldValue ? oldValue[key] : null
                const newKeyValue = newValue ? newValue[key] : null
                const newPath = (path && path.length > 0 ? `${path}.` : '') + key

                changes.push(
                    ...Helper.compareItem(oldKeyValue, newKeyValue, newPath)
                )
            }
        } else {
            if (oldValue === newValue) return changes

            if (!Helper.hasVal(oldValue)) {
                changes.push({
                    type: 'create',
                    prevValue: null,
                    newValue: newValue ?? null,
                    path: path ?? ''
                })
            } else if (!Helper.hasVal(newValue)) {
                changes.push({
                    type: 'delete',
                    prevValue: oldValue ?? null,
                    newValue: null,
                    path: path ?? ''
                })
            } else {
                changes.push({
                    type: 'update',
                    prevValue: oldValue ?? null,
                    newValue: newValue ?? null,
                    path: path ?? ''
                })
            }
        }

        return changes
    }

}

export default Helper
